import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ClinicRetentionSummary, MetricDefinitionId, OwnerDashboard, OwnerPvpRetentionDashboard, ReportFormat } from '@/application/metrics';
import { buildMonthVerdict } from '@/application/metrics';
import { isMonoClinicScope, soleClinicId } from '@/application/auth/clinicScope';
import {
  downloadComparisonReport,
  downloadDirectoryPack,
  getOwnerDashboardPair,
  getOwnerInsights,
  getOwnerPvpRetentionDashboard,
  type OwnerInsight,
} from '@/infrastructure/http/metricsApi';
import { metricsKeys } from '@/infrastructure/query/metricsKeys';
import { AttentionInsightList } from '@/presentation/components/domain/AttentionInsightList';
import { DashboardClinicShortcuts } from '@/presentation/components/domain/DashboardClinicShortcuts';
import { MonthVerdictBanner } from '@/presentation/components/domain/MonthVerdictBanner';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { PageSkeleton } from '@/presentation/components/common/PageSkeleton';
import { PanelPageState } from '@/presentation/components/common/PanelPageState';
import { DomainPageHeader } from '@/presentation/components/domain/DomainPageHeader';
import { ExportButtons } from '@/presentation/components/metrics/ExportButtons';
import { MetricKpiCard, MetricPrimaryStrip } from '@/presentation/components/metrics';
import { MetricsState } from '@/presentation/components/metrics/MetricsState';
import { formatInteger, formatPercent } from '@/presentation/components/metrics/metricFormatters';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';
import { useSubscriptionRiskChip } from '@/presentation/hooks/useSubscriptionRiskChip';
import { useMinWidthMd } from '@/presentation/hooks/useMinWidthMd';
import { formatMoneyCompact, formatSigned, relativeChangePct } from '@/presentation/utils/metricDeltas';
import { useAuth } from '@/presentation/contexts/useAuth';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { canExportReports } from '@/application/permissions/canExportReports';
import { firstVisibleHousePath } from '@/application/navigation/menuHouses';
import { COMPARISON_REPORT_PATH } from '@/application/reports/reportsCatalog';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { presentRetentionStatus } from '@/application/metrics/pvpRetentionPresentation';
import { OwnerStatusBadge } from '@/presentation/components/metrics/OwnerStatusBadge';

/** En viewports < md el fold muestra solo estos 3 (plata + pacientes + ausentismo). */
const MOBILE_FOLD_KPI_IDS = new Set(['revenue', 'patients', 'absenteeism']);

function formatKpiDelta(
  locale: string,
  kind: 'pct' | 'abs' | 'pp',
  delta: number | null,
  vsLabel: string,
  noDataLabel: string
): string {
  if (delta === null) {
    return noDataLabel;
  }
  if (kind === 'pct') {
    return `${formatSigned(locale, delta)}% ${vsLabel}`;
  }
  if (kind === 'pp') {
    return `${formatSigned(locale, delta)} pp ${vsLabel}`;
  }
  return `${formatSigned(locale, delta)} ${vsLabel}`;
}

interface ClinicOpsSnapshot {
  revenue: number;
  appointments: number;
  patients: number;
  absenteeism: number;
}

interface DashboardDataset {
  current: OwnerDashboard;
  previous: OwnerDashboard;
  retention: OwnerPvpRetentionDashboard;
  insights: OwnerInsight[];
  clinicOps: Map<string, ClinicOpsSnapshot>;
  previousOps: Map<string, ClinicOpsSnapshot>;
}

function statusRank(status: ClinicRetentionSummary['status']): number {
  if (status === 'critical') return 0;
  if (status === 'alert') return 1;
  if (status === 'observation') return 2;
  return 3;
}

function trendArrow(delta: number | null): 'up' | 'down' | 'flat' {
  if (delta === null || Math.abs(delta) < 0.5) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

function ClinicRetentionBadge({
  status,
  professionalsInAttention,
}: {
  status: ClinicRetentionSummary['status'];
  professionalsInAttention: number;
}) {
  const { t } = useTranslation();
  const presentation = presentRetentionStatus(status);
  return (
    <span className="inline-flex items-center gap-1">
      <OwnerStatusBadge
        label={t(presentation.labelKey)}
        tip={t(presentation.tipKey)}
        tone={presentation.tone}
        testId={`dashboard-clinic-status-${status}`}
      />
      {professionalsInAttention > 0 ? (
        <span className="panel-muted text-[10px] font-semibold tabular-nums">
          · {professionalsInAttention}
        </span>
      ) : null}
    </span>
  );
}

function queryErrorMessage(error: unknown): string | null {
  if (error instanceof Error && error.message) return error.message;
  if (error) return String(error);
  return null;
}

/** Centro de mando: veredicto + KPIs; mono = mi clínica; multi = red + ranking. */
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const { period, year, month, granularity, periodQuery, label: periodLabel } = useGlobalPeriod();
  const isMdUp = useMinWidthMd();

  const tenantIds = useMemo(() => (user?.scope.allowedTenantIds ?? []).filter(Boolean), [user?.scope.allowedTenantIds]);
  const isMono = isMonoClinicScope(tenantIds);
  const monoTenantId = soleClinicId(tenantIds);
  const locale = i18n.language;
  const canView = hasPermission('dashboard.view');
  const enabled = Boolean(token && tenantIds.length > 0 && canView);
  const showSubscriptionChip = useSubscriptionRiskChip(year, month, tenantIds);

  const pairRange =
    periodQuery.fromMonth !== undefined && periodQuery.toMonth !== undefined
      ? { fromMonth: periodQuery.fromMonth, toMonth: periodQuery.toMonth }
      : undefined;
  const pairQuery = useQuery({
    queryKey: metricsKeys.ownerDashboardPair(year, month, pairRange),
    queryFn: () => getOwnerDashboardPair(token!, periodQuery),
    enabled,
  });

  const retentionQuery = useQuery({
    queryKey: metricsKeys.ownerPvpRetention(year, month, 6),
    queryFn: () => getOwnerPvpRetentionDashboard(token!, { year, month, months: 6 }),
    enabled,
  });

  const insightsQuery = useQuery({
    queryKey: metricsKeys.ownerInsights(year, month, 6),
    queryFn: () => getOwnerInsights(token!, { year, month, months: 6 }),
    enabled,
  });

  /** Pair + retention son clave; insights es secundario (soft-degrade). */
  const coreLoading = pairQuery.isLoading || retentionQuery.isLoading;
  const coreError =
    queryErrorMessage(pairQuery.error) || queryErrorMessage(retentionQuery.error) || null;
  const insightsLoading = insightsQuery.isLoading;
  const insightsError = queryErrorMessage(insightsQuery.error);
  const insightsOk = Boolean(insightsQuery.data) && !insightsQuery.isError;

  const dataset = useMemo((): DashboardDataset | null => {
    if (!pairQuery.data || !retentionQuery.data) return null;
    const currentOwner = pairQuery.data.current;
    const previousOwner = pairQuery.data.previous;

    return {
      current: currentOwner,
      previous: previousOwner,
      retention: retentionQuery.data,
      insights: insightsOk ? (insightsQuery.data?.insights ?? []) : [],
      clinicOps: new Map(
        currentOwner.clinics.map((row) => [
          row.tenantId,
          {
            revenue: row.summary.caja.ingresos,
            appointments: row.summary.turnos.atendidos,
            patients: row.summary.pacientes.atendidos,
            absenteeism: row.summary.turnos.rates.absenteeism,
          } satisfies ClinicOpsSnapshot,
        ])
      ),
      previousOps: new Map(
        previousOwner.clinics.map((row) => [
          row.tenantId,
          {
            revenue: row.summary.caja.ingresos,
            appointments: row.summary.turnos.atendidos,
            patients: row.summary.pacientes.atendidos,
            absenteeism: row.summary.turnos.rates.absenteeism,
          } satisfies ClinicOpsSnapshot,
        ])
      ),
    };
  }, [pairQuery.data, retentionQuery.data, insightsQuery.data, insightsOk]);

  const handleRetryAll = useCallback(() => {
    void Promise.all([pairQuery.refetch(), retentionQuery.refetch(), insightsQuery.refetch()]);
  }, [pairQuery, retentionQuery, insightsQuery]);

  const handleRetryInsights = useCallback(() => {
    void insightsQuery.refetch();
  }, [insightsQuery]);

  const monthVerdict = useMemo(() => {
    if (!dataset) return null;
    const criticalInsightCount = dataset.insights.filter((item) => item.severity === 'critical').length;
    return buildMonthVerdict({
      revenueDeltaPct: relativeChangePct(
        dataset.current.totals.caja.ingresos,
        dataset.previous.totals.caja.ingresos
      ),
      clinicsInAttention: dataset.retention.totals.clinicsInAttention,
      totalClinics: dataset.current.totals.clinics || dataset.retention.clinics.length,
      criticalInsightCount,
      hasPreviousPeriod: true,
    });
  }, [dataset]);

  const globalKpis = useMemo(() => {
    if (!dataset) return [];
    const current = dataset.current.totals;
    const previous = dataset.previous.totals;
    const pvpDelta = dataset.retention.totals.networkPvpAverageDelta;
    const absenteeismDelta = Number(
      (current.turnos.rates.absenteeism - previous.turnos.rates.absenteeism).toFixed(1)
    );
    const vsLabel = t(
      granularity === 'year' ? 'dashboard.executive.vsPreviousYear' : 'dashboard.executive.vsPreviousMonth'
    );
    const noDataLabel = t('dashboard.command.noDelta');
    const revenueDelta = relativeChangePct(current.caja.ingresos, previous.caja.ingresos);
    const patientsDelta = relativeChangePct(current.pacientes.atendidos, previous.pacientes.atendidos);
    const appointmentsDelta = relativeChangePct(current.turnos.atendidos, previous.turnos.atendidos);

    return [
      {
        id: 'revenue' as MetricDefinitionId,
        label: t('dashboard.executive.kpis.revenue'),
        value: formatMoneyCompact(locale, current.caja.ingresos),
        kind: 'pct' as const,
        deltaLabel: formatKpiDelta(locale, 'pct', revenueDelta, vsLabel, noDataLabel),
      },
      {
        id: 'patients',
        label: t('dashboard.executive.kpis.patients'),
        value: formatInteger(current.pacientes.atendidos, locale),
        kind: 'pct' as const,
        deltaLabel: formatKpiDelta(locale, 'pct', patientsDelta, vsLabel, noDataLabel),
      },
      {
        id: 'appointments',
        label: t('dashboard.executive.kpis.appointments'),
        value: formatInteger(current.turnos.atendidos, locale),
        kind: 'pct' as const,
        deltaLabel: formatKpiDelta(locale, 'pct', appointmentsDelta, vsLabel, noDataLabel),
      },
      {
        id: 'pvp',
        label: t('dashboard.executive.kpis.pvp'),
        value: dataset.retention.totals.networkPvpAverage.toLocaleString(locale, {
          maximumFractionDigits: 1,
        }),
        kind: 'abs' as const,
        deltaLabel: formatKpiDelta(locale, 'abs', pvpDelta, vsLabel, noDataLabel),
        baseline:
          dataset.retention.totals.networkPvpAveragePrevious === null
            ? null
            : dataset.retention.totals.networkPvpAveragePrevious.toLocaleString(locale, {
                maximumFractionDigits: 1,
              }),
      },
      {
        id: 'absenteeism',
        label: t('dashboard.executive.kpis.absenteeism'),
        value: formatPercent(current.turnos.rates.absenteeism, locale),
        kind: 'pp' as const,
        deltaLabel: formatKpiDelta(locale, 'pp', absenteeismDelta, vsLabel, noDataLabel),
      },
    ].slice(0, 5);
  }, [dataset, locale, t, granularity]);

  const clinicRows = useMemo(() => {
    if (!dataset) return [];
    return dataset.retention.clinics
      .map((clinic) => {
        const ops = dataset.clinicOps.get(clinic.tenantId);
        const prev = dataset.previousOps.get(clinic.tenantId);
        const revDelta = ops && prev ? relativeChangePct(ops.revenue, prev.revenue) : null;
        return {
          tenantId: clinic.tenantId,
          label: clinicNames[clinic.tenantId] ?? clinic.tenantId,
          revenue: ops?.revenue ?? 0,
          pvp: clinic.clinicPvp,
          patients: ops?.patients ?? 0,
          absenteeism: ops?.absenteeism ?? 0,
          status: clinic.status,
          trend: trendArrow(revDelta ?? clinic.clinicPvpVariacionAbs),
          professionalsInAttention: clinic.professionalsInAttention,
        };
      })
      .sort((a, b) => statusRank(a.status) - statusRank(b.status) || b.revenue - a.revenue);
  }, [dataset, clinicNames]);

  const canExport = !isMono && canExportReports(hasPermission);
  const canExportDirectoryPack = canExportReports(hasPermission) && tenantIds.length > 0;
  const canOpenComparison = !isMono && hasPermission('reports.compare') && tenantIds.length >= 2;
  const handleExport = useCallback(
    (format: ReportFormat) => downloadComparisonReport(token ?? '', { year, month }, format),
    [token, year, month]
  );
  const handleDirectoryPack = useCallback(
    (format: ReportFormat) =>
      downloadDirectoryPack(token ?? '', { year, month }, format, {
        filename: t('dashboard.directoryPack.filename', { period }),
      }),
    [token, year, month, period, t]
  );

  const openClinic = (tenantId: string) => {
    navigate(`/clinics/${tenantId}?period=${period}`);
  };

  const headerSubtitle = isMono
    ? t('dashboard.command.subtitleMono', {
        period: periodLabel,
        clinic: (monoTenantId && clinicNames[monoTenantId]) || monoTenantId || '',
      })
    : t('dashboard.command.subtitle', {
        period: periodLabel,
        count: dataset?.current.totals.clinics ?? tenantIds.length,
      });

  const renderAttentionBlock = (hideClinicName: boolean) => {
    if (insightsLoading) {
      return (
        <>
          <h2 className="panel-section-title">
            {t('dashboard.executive.attention.title')}
          </h2>
          <div className="panel-soft-card">
            <LoadingBlock size="sm" minHeightClassName="min-h-[4rem]" />
          </div>
        </>
      );
    }
    if (insightsError) {
      return (
        <MetricsState
          variant="error"
          title={t('dashboard.executive.attention.title')}
          message={t('metrics.states.error.message')}
          onRetry={handleRetryInsights}
        />
      );
    }
    if (!insightsOk) return null;
    return (
      <AttentionInsightList
        insights={dataset?.insights ?? []}
        period={period}
        clinicNames={clinicNames}
        maxVisible={3}
        showSectionTitle
        hideClinicName={hideClinicName}
      />
    );
  };

  if (!canView) {
    return <AccessDenied domain="dashboard" homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  const showCoreError = Boolean(coreError) && !coreLoading;
  const showContent = Boolean(dataset);

  return (
    <div className="panel-page panel-safe-bottom bi-surface space-y-5">
      <DomainPageHeader title={t('dashboard.command.title')} subtitle={headerSubtitle}>
        {canExportDirectoryPack || canExport ? (
          <div className="flex flex-wrap items-center justify-end gap-3">
            {canExportDirectoryPack ? (
              <div data-testid="directory-pack-export">
                <ExportButtons
                  label={t('dashboard.directoryPack.label')}
                  onExport={handleDirectoryPack}
                  compact
                  touchFriendly
                />
              </div>
            ) : null}
            {canExport ? (
              <div data-testid="comparison-export">
                <ExportButtons onExport={handleExport} compact />
              </div>
            ) : null}
          </div>
        ) : null}
      </DomainPageHeader>

      {showCoreError ? (
        <PanelPageState
          variant="error"
          layout="inline"
          message={t('common.pageStates.error.message')}
          onRetry={handleRetryAll}
        />
      ) : null}

      {coreLoading && !dataset ? (
        <section className="space-y-3" aria-busy="true" data-testid="dashboard-loading">
          <PageSkeleton variant="executive" />
        </section>
      ) : null}

      {!coreLoading && !dataset && !showCoreError ? (
        <PanelPageState variant="empty" layout="inline" message={t('common.pageStates.empty.message')} />
      ) : null}

      {showContent ? (
        <>
          <section className="space-y-3">
            {monthVerdict ? (
              <MonthVerdictBanner
                verdict={monthVerdict}
                locale={locale}
                variant={isMono ? 'mono' : 'multi'}
              />
            ) : null}

            {showSubscriptionChip ? (
              <Link
                to={`/subscription?period=${period}`}
                data-testid="subscription-risk-chip"
                className="inline-flex w-fit items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200 dark:hover:bg-red-900/60"
              >
                {t('subscription.dashboardChip')}
              </Link>
            ) : null}

            {!isMono ? <section className="space-y-1.5">{renderAttentionBlock(false)}</section> : null}

            {(() => {
              const foldKpis = globalKpis.filter((kpi) => MOBILE_FOLD_KPI_IDS.has(kpi.id));
              const moreKpis = globalKpis.filter((kpi) => !MOBILE_FOLD_KPI_IDS.has(kpi.id));
              const kpiTone = (id: string) => {
                if (id === 'revenue') return 'green' as const;
                if (id === 'appointments') return 'blue' as const;
                if (id === 'pvp') return 'indigo' as const;
                if (id === 'absenteeism') return 'amber' as const;
                return 'slate' as const;
              };
              const renderKpi = (
                kpi: (typeof globalKpis)[number],
                variant: 'primary' | 'compact' = 'primary'
              ) => (
                <MetricKpiCard
                  key={kpi.id}
                  variant={variant}
                  data-kpi-id={kpi.id}
                  label={kpi.label}
                  value={kpi.value}
                  detail={
                    <>
                      {kpi.deltaLabel}
                      {kpi.kind === 'abs' && kpi.baseline ? (
                        <span className="block text-[10px] opacity-80 sm:text-[11px]">
                          {t('dashboard.command.prevBaseline', { value: kpi.baseline })}
                        </span>
                      ) : null}
                    </>
                  }
                  tone={kpiTone(kpi.id)}
                  metricId={kpi.id}
                />
              );

              if (isMdUp) {
                return (
                  <MetricPrimaryStrip
                    data-testid="dashboard-kpi-strip-desktop"
                    aria-label={t('dashboard.command.kpiStripLabel')}
                  >
                    {globalKpis.map(renderKpi)}
                  </MetricPrimaryStrip>
                );
              }

              return (
                <>
                  <MetricPrimaryStrip
                    className="grid-cols-3"
                    data-testid="dashboard-kpi-strip"
                    aria-label={t('dashboard.command.kpiStripLabel')}
                  >
                    {foldKpis.map(renderKpi)}
                  </MetricPrimaryStrip>
                  {moreKpis.length > 0 ? (
                    <div className="space-y-1.5" data-testid="dashboard-kpi-more">
                      <h2 className="panel-section-title panel-section-title--dense">
                        {t('dashboard.command.moreKpis')}
                      </h2>
                      <MetricPrimaryStrip className="grid-cols-2">
                        {moreKpis.map((kpi) => renderKpi(kpi, 'compact'))}
                      </MetricPrimaryStrip>
                    </div>
                  ) : null}
                </>
              );
            })()}
          </section>

          {isMono && monoTenantId ? (
            <DashboardClinicShortcuts tenantId={monoTenantId} period={period} />
          ) : null}

          {isMono ? <section className="space-y-1.5">{renderAttentionBlock(true)}</section> : null}

          {!isMono ? (
            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="panel-section-title">
                  {t('dashboard.executive.clinicsTitle')}
                </h2>
                <div className="flex flex-wrap items-center gap-3">
                  {canOpenComparison ? (
                    <Link
                      to={COMPARISON_REPORT_PATH}
                      data-testid="compare-clinics-cta"
                      className="panel-link text-xs font-semibold hover:underline"
                    >
                      {t('dashboard.command.compareClinics')}
                    </Link>
                  ) : null}
                  {tenantIds.length > 1 && (
                    <button
                      type="button"
                      onClick={() => navigate(`/clinics?period=${period}`)}
                      className="panel-link text-xs font-semibold hover:underline"
                    >
                      {t('dashboard.command.viewAllClinics')}
                    </button>
                  )}
                </div>
              </div>

              {clinicRows.length === 0 ? (
                <MetricsState
                  variant="empty"
                  title={t('dashboard.command.noClinics')}
                  message={t('metrics.states.empty.message')}
                />
              ) : (
                <>
                  {/* Cards hasta lg: un celu horizontal (~844px) cruza md y si no
                   * se iba a la tabla con scroll de costado. */}
                  <ul className="space-y-2 lg:hidden">
                    {clinicRows.map((row) => (
                      <li key={row.tenantId}>
                        <div className="panel-soft-card w-full p-3">
                          <div className="flex items-start justify-between gap-2">
                            <button
                              type="button"
                              onClick={() => openClinic(row.tenantId)}
                              className="panel-heading min-w-0 text-left font-semibold"
                            >
                              {row.label}
                            </button>
                            <ClinicRetentionBadge
                              status={row.status}
                              professionalsInAttention={row.professionalsInAttention}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => openClinic(row.tenantId)}
                            className="mt-2 w-full text-left transition active:scale-[0.99]"
                          >
                            <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                              {[
                                {
                                  key: 'revenue',
                                  label: t('dashboard.command.table.revenue'),
                                  value: formatMoneyCompact(locale, row.revenue),
                                },
                                {
                                  key: 'pvp',
                                  label: t('dashboard.command.table.pvp'),
                                  value: `${row.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })} ${
                                    row.trend === 'up' ? '↑' : row.trend === 'down' ? '↓' : '→'
                                  }`,
                                },
                                {
                                  key: 'patients',
                                  label: t('dashboard.command.table.patients'),
                                  value: formatInteger(row.patients, locale),
                                },
                                {
                                  key: 'absenteeism',
                                  label: t('dashboard.command.table.absenteeism'),
                                  value: formatPercent(row.absenteeism, locale),
                                },
                              ].map((metric) => (
                                <div key={metric.key} className="min-w-0">
                                  <dt className="panel-muted truncate text-[10px] uppercase tracking-wide">
                                    {metric.label}
                                  </dt>
                                  <dd className="panel-body break-words text-sm font-semibold tabular-nums">
                                    {metric.value}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* Tablet ancha / desktop (>= lg): tabla completa. */}
                  <div className="metric-secondary-panel hidden lg:block">
                    <table className="min-w-full text-left">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 font-semibold">{t('dashboard.command.table.clinic')}</th>
                        <th className="px-3 py-2 font-semibold">{t('dashboard.command.table.revenue')}</th>
                        <th className="px-3 py-2 font-semibold">{t('dashboard.command.table.pvp')}</th>
                        <th className="px-3 py-2 font-semibold">{t('dashboard.command.table.patients')}</th>
                        <th className="px-3 py-2 font-semibold">{t('dashboard.command.table.absenteeism')}</th>
                        <th className="px-3 py-2 font-semibold">{t('dashboard.command.table.trend')}</th>
                        <th className="px-3 py-2 font-semibold">{t('dashboard.command.table.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clinicRows.map((row) => (
                          <tr
                            key={row.tenantId}
                            className="panel-row-hover cursor-pointer border-b border-[var(--color-border)] last:border-0"
                            onClick={() => openClinic(row.tenantId)}
                          >
                            <td className="panel-heading px-3 py-2.5 font-semibold">
                              {row.label}
                            </td>
                            <td className="panel-body px-3 py-2.5 tabular-nums">
                              {formatMoneyCompact(locale, row.revenue)}
                            </td>
                            <td className="panel-body px-3 py-2.5 tabular-nums">
                              {row.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })}
                            </td>
                            <td className="panel-body px-3 py-2.5 tabular-nums">
                              {formatInteger(row.patients, locale)}
                            </td>
                            <td className="panel-body px-3 py-2.5 tabular-nums">
                              {formatPercent(row.absenteeism, locale)}
                            </td>
                            <td className="panel-body px-3 py-2.5">
                              {row.trend === 'up' ? '↑' : row.trend === 'down' ? '↓' : '→'}
                            </td>
                            <td className="px-3 py-2.5">
                              <ClinicRetentionBadge
                                status={row.status}
                                professionalsInAttention={row.professionalsInAttention}
                              />
                            </td>
                          </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </>
              )}
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
